import React, { useEffect, useState } from 'react';
import { marketing } from '../services/api';
import { toast } from '../hooks/use-toast';
import { X, Loader2, CheckCircle2, Mail, Sparkles } from 'lucide-react';

/**
 * Combined sign-up + AI-survey modal that opens when a visitor ticks the
 * "Send me deals from [Business Name]" checkbox on a Tier-3 slider card.
 *
 * Flow:
 *  1. Fetches the live form config (name/email/phone fields + 3-5 AI-generated
 *     questions) for the given businessId.
 *  2. User fills it in; we POST to /slider/signup-form/{businessId}/submit.
 *  3. Backend either flags for admin review or sends a Resend double-opt-in
 *     email. We show a "Check your email" screen.
 */
export default function SliderSignupModal({ businessId, businessName, onClose }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { status, message }
  const [values, setValues] = useState({ name: '', email: '', phone: '', answers: {} });

  useEffect(() => {
    (async () => {
      try {
        const f = await marketing.sliderFormPublic(businessId);
        setForm(f);
      } catch (err) {
        toast({ title: 'Form unavailable', description: err?.response?.data?.detail || 'Try again later.' });
        onClose?.();
      } finally { setLoading(false); }
    })();
  }, [businessId]); // eslint-disable-line

  const setAnswer = (qid, v) => setValues(prev => ({ ...prev, answers: { ...prev.answers, [qid]: v } }));

  const submit = async () => {
    if (!form) return;
    const required = form.fields || {};
    if (required.name?.enabled && required.name?.required && !values.name.trim()) {
      toast({ title: 'Your name is required.' }); return;
    }
    if (!values.email.trim() || !values.email.includes('@')) {
      toast({ title: 'A valid email is required.' }); return;
    }
    if (required.phone?.enabled && required.phone?.required && !values.phone.trim()) {
      toast({ title: 'Phone is required.' }); return;
    }
    for (const q of (form.questions || [])) {
      if (q.required) {
        const v = values.answers[q.id];
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) {
          toast({ title: `Please answer: ${q.prompt}` }); return;
        }
      }
    }
    setSubmitting(true);
    try {
      const answers = Object.entries(values.answers).map(([questionId, value]) => ({ questionId, value }));
      const r = await marketing.sliderFormSubmit(businessId, {
        name: values.name.trim() || null,
        email: values.email.trim(),
        phone: values.phone.trim() || null,
        answers,
      });
      setDone({ status: r.status, message: r.message });
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSubmitting(false); }
  };

  const fields = form?.fields || {};
  const questions = form?.questions || [];

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4"
         data-testid="slider-signup-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-green-600 to-emerald-700 text-white">
          <div className="font-bold text-base flex items-center gap-2"><Sparkles size={16}/> Deals from {businessName}</div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="slider-signup-close"><X size={18}/></button>
        </div>

        {loading && (
          <div className="p-10 flex items-center justify-center text-gray-500">
            <Loader2 size={20} className="animate-spin mr-2"/> Loading…
          </div>
        )}

        {!loading && done && (
          <div className="p-8 text-center space-y-3" data-testid="slider-signup-done">
            <CheckCircle2 size={44} className="text-green-600 mx-auto"/>
            <div className="text-lg font-bold text-gray-900">
              {done.status === 'pending_confirm' ? 'Check your inbox' : 'Thanks for signing up'}
            </div>
            <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
              <Mail size={13}/> {done.message}
            </div>
            <button onClick={onClose} className="mt-3 px-5 py-2 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">
              Done
            </button>
          </div>
        )}

        {!loading && !done && form && (
          <div className="p-5 overflow-y-auto space-y-4">
            <div className="text-xs text-gray-500 leading-relaxed">
              Tell us a little about yourself so <b>{businessName}</b> can send you offers you'll actually want. We'll email a quick confirmation link.
            </div>

            {fields.name?.enabled !== false && (
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">
                  {fields.name?.label || 'Full name'} {fields.name?.required && <span className="text-red-500">*</span>}
                </label>
                <input value={values.name} onChange={e => setValues({ ...values, name: e.target.value })}
                  data-testid="slider-signup-name"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"/>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">
                {fields.email?.label || 'Email'} <span className="text-red-500">*</span>
              </label>
              <input type="email" value={values.email} onChange={e => setValues({ ...values, email: e.target.value })}
                data-testid="slider-signup-email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"/>
            </div>

            {fields.phone?.enabled !== false && (
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">
                  {fields.phone?.label || 'Phone'} {fields.phone?.required && <span className="text-red-500">*</span>}
                </label>
                <input type="tel" value={values.phone} onChange={e => setValues({ ...values, phone: e.target.value })}
                  data-testid="slider-signup-phone"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"/>
              </div>
            )}

            {questions.length > 0 && <div className="h-px bg-gray-200 my-2"/>}

            {questions.map((q, idx) => (
              <QuestionRow key={q.id || idx} q={q} value={values.answers[q.id]} onChange={v => setAnswer(q.id, v)} />
            ))}

            <div className="pt-2 border-t border-gray-100">
              <button onClick={submit} disabled={submitting}
                data-testid="slider-signup-submit"
                className="w-full px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting && <Loader2 size={14} className="animate-spin"/>}
                Send me deals
              </button>
              <div className="text-[10px] text-gray-400 text-center mt-2">
                We'll email a confirmation link. You can unsubscribe anytime.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionRow({ q, value, onChange }) {
  const req = q.required ? <span className="text-red-500 ml-1">*</span> : null;
  if (q.type === 'single_choice') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-800 block mb-1.5">{q.prompt}{req}</label>
        <div className="space-y-1.5">
          {(q.options || []).map((opt, i) => (
            <label key={i} className="flex items-center gap-2 text-sm cursor-pointer text-gray-700">
              <input type="radio" name={q.id} checked={value === opt} onChange={() => onChange(opt)}
                className="accent-green-600"/>
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === 'multi_choice') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt]);
    return (
      <div>
        <label className="text-xs font-semibold text-gray-800 block mb-1.5">{q.prompt}{req}</label>
        <div className="space-y-1.5">
          {(q.options || []).map((opt, i) => (
            <label key={i} className="flex items-center gap-2 text-sm cursor-pointer text-gray-700">
              <input type="checkbox" checked={arr.includes(opt)} onChange={() => toggle(opt)}
                className="accent-green-600"/>
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === 'rating') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-800 block mb-1.5">{q.prompt}{req}</label>
        <div className="flex gap-1.5">
          {[1,2,3,4,5].map(n => (
            <button key={n} type="button" onClick={() => onChange(n)}
              className={`w-9 h-9 rounded-full font-bold text-sm ${value === n ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {n}
            </button>
          ))}
        </div>
      </div>
    );
  }
  // text
  return (
    <div>
      <label className="text-xs font-semibold text-gray-800 block mb-1.5">{q.prompt}{req}</label>
      <textarea rows={2} value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"/>
    </div>
  );
}
